import { Injectable } from "@angular/core";
import { LogicTreesHandlerService } from "../handlers/logic-trees-handler.service";
import { SessionService } from "../session.service";
import { camelCase, get, union, filter, cloneDeep, sortBy, map } from "lodash";
import { AccountUserGroupService } from "../account-user-group/account-user-group.service";
import { first, take } from "rxjs/operators";
import { BehaviorSubject } from "rxjs";
import { SignalsService } from "../signals/signals.service";
import { UtilService } from "../util/util.service";
import { GeofenceService } from "../geofences/geofence.service";
import { BeaconRegionsService } from "../beacon-regions/beacon-regions.service";
import { IntegrationsService } from "../integrations/integrations.service";
import { UiService } from "../ui/ui.service";
import { HttpClient } from "@angular/common/http";

const CONDITIONS: any[] = [

  /*

  Keep the follow-up conditions first because the required follow-up condition is automatically added.  This should be changed.

  */

  // Signal Follow-Up
  {
    group: "Signal",
    name: "timeAfterSignalSent",
    label: "Time After Sent",
    type: "timeAfterSent",
    id: "timeAfterSent",
    value: "Signal.timeAfterSent",
    triggers: [],
    internal: false,
    followUp: true,
    followUpType: "phone",
    comparisons: [
      {
        name: "Equal",
        operator: "===",
      },
    ],
  },
  {
    id: "signalRead",
    group: "Signal",
    name: "signalRead",
    label: "Signal Was Read",
    type: "signalRead",
    value: "Signal.read",
    triggers: [],
    internal: false,
    followUp: true,
    followUpType: "phone",
    comparisons: [
      {
        name: "Equal",
        operator: "===",
      },
    ],
  },

  // Email Follow-Up
  {
    group: "Email",
    label: "Time After Sent",
    name: "timeAfterEmailSent",
    type: "timeAfterSent",
    id: "timeAfterSent",
    value: "EmailSent.timeAfterSent",
    triggers: [],
    followUp: true,
    followUpType: "email",
    comparisons: [
      {
        name: "Equal",
        operator: "===",
      },
    ],
  },
  {
    id: "emailStatus",
    group: "Email",
    name: "emailStatus",
    label: "Email Status",
    type: "emailStatus",
    value: "EmailSent.status",
    triggers: [],
    followUp: true,
    followUpType: "email",
    comparisons: [
      {
        name: "Equal",
        operator: "===",
      },
    ],
  },
  {
    id: "emailDeliveredAt",
    group: "Email",
    name: "emailDeliveredAt",
    label: "Email Delivery Date",
    type: "date",
    _type: "Date",
    value: "EmailSent.delivered_at",
    triggers: [],
    followUp: true,
    followUpType: "email",
  },
  {
    id: "emailOpenCount",
    group: "Email",
    name: "emailOpenCount",
    label: "Email Open Count",
    type: "integer",
    _type: "Integer",
    value: "EmailSent.open_count",
    triggers: [],
    followUp: true,
    followUpType: "email",
  },
  {
    id: "emailLastOpenDate",
    group: "Email",
    name: "emailLastOpenAt",
    label: "Email Last Open Date",
    type: "date",
    _type: "Date",
    value: "EmailSent.last_open_at",
    triggers: [],
    followUp: true,
    followUpType: "email",
  },
  {
    id: "emailClickCount",
    group: "Email",
    name: "emailClickCount",
    label: "Email Click Count",
    type: "integer",
    _type: "Integer",
    value: "EmailSent.click_count",
    triggers: [],
    followUp: true,
    followUpType: "email",
  },
  {
    id: "emailLastClickDate",
    group: "Email",
    name: "emailLastClickAt",
    label: "Email Last Click Date",
    type: "date",
    _type: "Date",
    value: "EmailSent.last_click_at",
    triggers: [],
    followUp: true,
    followUpType: "email",
  },

  // App
  {
    group: "Device",
    name: "isRegistered",
    label: "Device is Registered to User",
    type: "isNull",
    _type: "Boolean",
    value: "Device.account_app_user_id",
    triggers: ['AppInstall','ProfileUpdate','AppUse','LocationUpdate','LastSignal','AppPermissionsUpdate','GeofenceEntry','GeofenceExit','BeaconRegionEntry','BeaconRegionExit','OnProperty','OffProperty'],
    internal: false
  },
  {
    group: "Device",
    name: "permissionLocation",
    label: "Location Permission",
    type: "locationPermission",
    value: "Device.location_permission",
    triggers: ['AppInstall','ProfileUpdate','AppUse','LocationUpdate','LastSignal','AppPermissionsUpdate','GeofenceEntry','GeofenceExit','BeaconRegionEntry','BeaconRegionExit','OnProperty','OffProperty'],
    internal: false
  },
  {
    group: "Device",
    name: "permissionNotification",
    label: "Notification Permission Granted",
    type: "boolean",
    _type: "Boolean",
    value: "Device.allows_push",
    triggers: ['AppInstall','ProfileUpdate','AppUse','LocationUpdate','LastSignal','AppPermissionsUpdate','GeofenceEntry','GeofenceExit','BeaconRegionEntry','BeaconRegionExit','OnProperty','OffProperty'],
    internal: false
  },
  {
    group: "Device",
    name: "lastVisit",
    label: "Last Visit",
    type: "lastVisit",
    value: "User.info.last_visit",
    triggers: ['AppInstall','ProfileUpdate','AppUse','LocationUpdate','LastSignal','AppPermissionsUpdate','GeofenceEntry','GeofenceExit','BeaconRegionEntry','BeaconRegionExit','OnProperty','OffProperty'],
    internal: false
  },
  {
    group: "Device",
    name: "timeOnStation",
    label: "Time on station",
    type: "timeOnStation",
    value: "User.info.time_on_station",
    triggers: ['GeofenceEntry','GeofenceExit','BeaconRegionEntry','BeaconRegionExit','OnProperty','OffProperty'],
    internal: false
  },
  {
    group: "Time",
    name: "dayOfWeek",
    label: "Day of Week",
    type: "dayOfWeek",
    value: "User.info.day_of_week",
    triggers: []
  },
  {
    group: "Time",
    name: "timeOfDay",
    label: "Time of day",
    type: "timeOfDay",
    value: "User.info.time_of_day",
    triggers: []
  },

  // INTERNAL ONLY
  {
    group: "Account",
    name: "accountCreateDate",
    label: "Account Creation Date",
    type: "date",
    _type: "Date",
    value: "Account.created_at",
    triggers: ['AccountCreated','UserCreated','UserLogin','FlowCreated','BroadcastCreated'],
    internal: true
  },
  {
    group: "Account",
    name: "userCount",
    label: "Count of Account Users",
    type: "integer",
    _type: "Integer",
    value: "Account.user_count",
    triggers: ['AccountCreated','UserCreated','UserLogin','FlowCreated','BroadcastCreated'],
    internal: true
  },
  {
    group: "Account",
    name: "flowCount",
    label: "Count of Account Flows",
    type: "integer",
    _type: "Integer",
    value: "Account.flows_count",
    triggers: ['AccountCreated','UserCreated','UserLogin','FlowCreated','BroadcastCreated'],
    internal: true
  },
  {
    group: "Account",
    name: "broadcastCount",
    label: "Count of Account Broadcasts",
    type: "integer",
    _type: "Integer",
    value: "Account.broadcasts_count",
    triggers: ['AccountCreated','UserCreated','UserLogin','FlowCreated','BroadcastCreated'],
    internal: true
  },
  {
    group: "Account",
    name: "geofenceCount",
    label: "Count of Account Geofences",
    type: "integer",
    _type: "Integer",
    value: "Account.geofence_count",
    triggers: ['AccountCreated','UserCreated','UserLogin','FlowCreated','BroadcastCreated'],
    internal: true
  },
  {
    group: "Account",
    name: "beaconRegionCount",
    label: "Count of Account Beacon Regions",
    type: "integer",
    _type: "Integer",
    value: "Account.beacon_region_count",
    triggers: ['AccountCreated','UserCreated','UserLogin','FlowCreated','BroadcastCreated'],
    internal: true
  },
  {
    group: "AccountUser",
    name: "accountUserCreateDate",
    label: "Account User Creation Date",
    type: "date",
    _type: "Date",
    value: "AccountUser.created_at",
    triggers: ['UserCreated','UserLogin'],
    internal: true
  },
  {
    group: "AccountUser",
    name: "lastLoginDate",
    label: "Account User Last Login Date",
    type: "date",
    _type: "Date",
    value: "AccountUser.last_login_at",
    triggers: ['UserCreated','UserLogin'],
    internal: true
  },
  {
    group: "Flow",
    name: "flowCreateDate",
    label: "Flow Creation Date",
    type: "date",
    _type: "Date",
    value: "LogicTree.created_at",
    triggers: ['FlowCreated'],
    internal: true
  },
  {
    group: "Broadcast",
    name: "broadcastCreateDate",
    label: "Broadcast Creation Date",
    type: "date",
    _type: "Date",
    value: "QuickSignal.created_at",
    triggers: ['BroadcastCreated'],
    internal: true
  },
  {
    group: "Broadcast",
    name: "sendDate",
    label: "Broadcast Send Date",
    type: "date",
    _type: "Date",
    value: "QuickSignal.send_date_time",
    triggers: ['BroadcastCreated'],
    internal: true
  },
];

const DEFAULT_FILTER_OPTIONS = [
  {
    color: "#fff",
    label: "None",
    value: 0,
    points: {
      lower: 0,
      upper: 1000,
    },
  },
  {
    color: "#cc724a",
    label: "Bronze",
    value: 1,
    points: {
      lower: 1001,
      upper: 5000,
    },
  },
  {
    color: "#747474",
    label: "Silver",
    value: 2,
    points: {
      lower: 5001,
      upper: 10000,
    },
  },
  {
    color: "#f6b300",
    label: "Gold",
    value: 3,
    points: {
      lower: 10001,
      upper: 20000,
    },
  },
  {
    color: "#b4b1ac",
    label: "Platinum",
    value: 4,
    points: {
      lower: 20001,
      upper: 50000,
    },
  },
];

//TODO SPLIT OUT CACHE INTO ITS OWN SERVICE
//TODO CACHE CALLS INDIVIDUALLY

@Injectable({
  providedIn: "root",
})
export class FlowsService {

  private phoneCacheInitiated = false;
  private starterCacheInitiated = false;
  private notificationCacheInitiated = false;
  private integrationCacheInitiated = false;

  locationPermissionOptions: (
    | { value: null; label: string }
    | { value: number; label: string }
  )[] = [
    {
      value: -1,
      label: "Unknown",
    },
    {
      value: 2,
      label: "Denied",
    },
    {
      value: 3,
      label: "Always Allowed",
    },
    {
      value: 4,
      label: "Only When Open",
    },
    {
      value: 1,
      label: "Only Once",
    },
  ];
  emailStatusOptions: (
    | { value: null; label: string }
    | { value: string; label: string }
  )[] = [
    {
      value: "processing",
      label: "Processing",
    },
    {
      value: "delivered",
      label: "Delivered",
    },
    {
      value: "bounced",
      label: "Bounced",
    },
  ];
  booleanOptions: { name: string; code: boolean }[] = [
    {
      name: "Yes",
      code: true,
    },
    {
      name: "No",
      code: false,
    },
  ];
  dayOfWeekOptions: string[] = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];
  notificationOptions!: any[];
  accountUserGroups: any;

  cacheReady = new BehaviorSubject<boolean | null>(null);
  phoneCacheReady = new BehaviorSubject<boolean | null>(null);
  starterCacheReady = new BehaviorSubject<boolean | null>(null);
  notificationCacheReady = new BehaviorSubject<boolean | null>(null);
  emailCacheReady = new BehaviorSubject<boolean | null>(null);

  templateOptions: any;
  trackingCategoryOptions: any;
  tags: any;
  locationOptions: any;
  triggerOptions: any;
  integrationOptions: any;
  conversionTrackingOptions?: {
    label: string;
    value: string;
  }[];
  minuteOptions: string[] = ["00","01","02","03","04","05","06","07","08","09","10","11","12","13","14","15","16","17","18","19","20","21","22","23","24","25","26","27","28","29","30","31","32","33","34","35","36","37","38","39","40","41","42","43","44","45","46","47","48","49","50","51","52","53","54","55","56","57","58","59"];
  hourOptions: string[] = [
    "12",
    "01",
    "02",
    "03",
    "04",
    "05",
    "06",
    "07",
    "08",
    "09",
    "10",
    "11",
  ];
  pmOptions: { name: string; code: boolean }[] = [
    {
      name: "AM",
      code: false,
    },
    {
      name: "PM",
      code: true,
    },
  ];
  //Used to check if any panzoom areas are currently initiated
  panzoomInitiated = new BehaviorSubject<boolean>(false);
  currentFlowTrigger: any;

  constructor(
    private LogicTreesHandlerService: LogicTreesHandlerService,
    private SessionService: SessionService,
    private AccountUserGroupService: AccountUserGroupService,
    private SignalsService: SignalsService,
    private UtilService: UtilService,
    private GeofenceService: GeofenceService,
    private BeaconRegionsService: BeaconRegionsService,
    private IntegrationsService: IntegrationsService,
    private uiService: UiService,
    private httpClient: HttpClient
  ) {}

  private get currentAccount() {
    return this.SessionService.currentAccountValue!;
  }

  private get currentApp() {
    return this.SessionService.currentAppValue!;
  }

  //LIST
  getTriggerEvents(
    masterAccountID: number,
    queryParams?: {
      page?: number;
      perPage?: number;
    },
  ) {
    return this.LogicTreesHandlerService.getEntryEvents(
      masterAccountID,
      queryParams,
    );
  }

  getIntegrations(
    masterAccountID?: number,
    accountAppID?: number,
    type?: string
  ) {
    return this.IntegrationsService.getIntegrations(masterAccountID, accountAppID, false, type )
  }

  getLogicTrees(
    masterAccountID: number,
    queryParams?: {
      facility_id?: number[];
      page?: number;
      perPage?: number;
      filter_columns?: string[];
    },
  ) {
    return this.LogicTreesHandlerService.getLogicTrees(
      masterAccountID,
      queryParams,
    );
  }

  getLogicTree(masterAccountID: number, logicTreeID: number) {
    return this.LogicTreesHandlerService.getLogicTree(
      masterAccountID,
      logicTreeID,
    );
  }

  deleteLogicTree(masterAccountID: number, logicTreeID: number) {
    return this.LogicTreesHandlerService.deleteLogicTree(
      masterAccountID,
      logicTreeID,
    );
  }

  updateLogicTree(
    masterAccountID: number,
    logicTreeID: number,
    logicTree: any,
  ) {
    return this.LogicTreesHandlerService.updateLogicTree(
      masterAccountID,
      logicTreeID,
      logicTree,
    );
  }

  createLogicTree(masterAccountID: number, logicTree: any) {
    return this.LogicTreesHandlerService.createLogicTree(
      masterAccountID,
      logicTree,
    );
  }

  //LIST
  getRedemptions(
    masterAccountID: number,
    accountAppID: number,
    queryParams?: {
      facility_id?: number[];
      start?: string;
      end?: string;
      page?: number;
      perPage?: number;
    },
  ) {
    return this.LogicTreesHandlerService.getRedemptions(
      masterAccountID,
      accountAppID,
      queryParams,
    );
  }

  getReport(masterAccountID: number, logicTreeID: number, queryParams?: any) {
    return this.LogicTreesHandlerService.getReport(
      masterAccountID,
      logicTreeID,
      queryParams,
    );
  }

  getPayloadLookup(masterAccountID: number, name: string, query: string) {
    return this.LogicTreesHandlerService.getPayloadLookup(
      masterAccountID,
      name,
      query,
    );
  }

  getFrequencies(type: any): any {
    return [{
      label: "Always Send",
      value: "always",
    },
    {
      group: "Frequency",
      name: "freqOnce",
      label: "Send Once",
      type: "signal",
      operator: "==",
      value: "once",
    },
    {
      group: "Frequency",
      name: "freqOnceHour",
      label: "Send Once Every Hour",
      type: "signal",
      operator: "==",
      value: "onceLast1",
    },
    {
      group: "Frequency",
      name: "freqOnceEvery2Hour",
      label: "Send Once Every 2 Hours",
      type: "signal",
      operator: "==",
      value: "onceLast2",
    },
    {
      group: "Frequency",
      name: "freqOnceEvery4Hour",
      label: "Send Once Every 4 Hours",
      type: "signal",
      operator: "==",
      value: "onceLast4",
    },
    {
      group: "Frequency",
      name: "freqOnceEvery6Hour",
      label: "Send Once Every 6 Hours",
      type: "signal",
      operator: "==",
      value: "onceLast6",
    },
    {
      group: "Frequency",
      name: "freqOnceEvery12Hour",
      label: "Send Once Every 12 Hours",
      type: "signal",
      operator: "==",
      value: "onceLast12",
    },
    {
      group: "Frequency",
      name: "freqOnceEveryDay",
      label: "Send Once Every 24 Hours",
      type: "signal",
      operator: "==",
      value: "onceLast24",
    },
  
    {
      group: "Frequency",
      name: "freqOnceEveryWeek",
      label: "Send Once Per Week",
      type: "signal",
      operator: "==",
      value: "onceLast1Week",
    },
    {
      group: "Frequency",
      name: "freqOnceEvery15Days",
      label: "Send Once Every 15 Days",
      type: "signal",
      operator: "==",
      value: "onceLast15Days",
    },
    {
      group: "Frequency",
      name: "freqOnceEvery30Days",
      label: "Send Once Every 30 Days",
      type: "signal",
      operator: "==",
      value: "onceLast30Days",
    },];
  }

  // Break out object and object[]
  extractTraits(trait: any, results: any) : any {
    let t = cloneDeep(trait);
    if(t._type === "Object" || t._type === "Object[]"){
      for(let subTrait of t.fields){
        //
        subTrait.id = t.id + "." + subTrait.id;
        subTrait.field = t.field + (t._type == "Object[]" ? "[]" : "") + "." + subTrait.field;
        subTrait.name = t.name + " -> " + subTrait.name;
        let subTraits = this.extractTraits(subTrait, results);
        results = subTraits;
      }
    }
    else {
      results.push(t);
    }
    return results;
  }

  getConditions(internal: boolean, trigger: any, sourceType: any, targetType: any, followUp: boolean) {

    //console.log(`GET CONDITIONS Internal? ${internal}, Trigger: ${JSON.stringify(trigger, null, 2)}, Source Type: ${JSON.stringify(sourceType, null, 2)}, Target Type: ${JSON.stringify(targetType, null, 2)}, Follow-Up? ${followUp}`)

    //this.minuteOptions;

    let conditions: any[] = filter(CONDITIONS, (c)=>{
      var result = (c.internal == internal || c.internal === undefined) && 
        (c.triggers.indexOf(trigger.name) > -1 || c.triggers.length == 0) && 
        (c.followUp == followUp || c.followUp === undefined) && 
        ((followUp && sourceType == c.followUpType) || !followUp || c.followUp === undefined)
      //console.log(`${c.name}: ${(c.internal == internal || c.internal === undefined)} + ${(c.triggers.indexOf(trigger.name) > -1 || c.triggers.length == 0)} + ${(c.followUp == followUp || c.followUp == undefined)} + ${((followUp && sourceType == c.followUpType) || !followUp || c.followUp === undefined)} = ${result}`)
      return result
    })

    //console.log('GET CONDITIONS', conditions)

    // NOT INTERNAL
    if (!internal) {

      if (this.currentAccount?.catalog?.app_users?.profile) {
        let profileConditionItems = [];

        //console.log('PROFILE FIELDS', this.currentAccount.catalog.app_users.profile)

        let rawTraits : any[] = [];
        for (let trait of this.currentAccount.catalog.app_users.profile) {
          let results = this.extractTraits(trait, []);
          results.forEach((result: any) => {
            rawTraits.push(result);
          });
        }

        //console.log('RAW TRAITS', rawTraits)

        const traits = rawTraits.filter(
          (field: any) => {
            return (
              field.signal_condition === undefined ||
              field.signal_condition === true
            );
          },
        );

        profileConditionItems = traits.map((f: any) => {
          //console.log('PROFILE FIELD', f)
          let options: any[] = [];

          if(f.options !== undefined && f.options.length > 0){
            options = f.options;
            if(f.sortBy !== undefined){
              options = sortBy(options, f.sortBy);
            } else {
              options = sortBy(options, 'label');
            }
          } else if (f.ref) {
            switch(f.ref.type){
              case "facility":
                options = map(sortBy(this.SessionService.facilitiesValue!, 'name'), (facility: any) => {
                  return {
                    label: facility.name,
                    value: facility.foreign_id,
                  };
                });
              break;
            }
          }

          return {
            group: "User",
            label: f.name,
            type: options.length > 0 ? (f._type.indexOf('[]') > - 1 ? 'multi-select' : 'select') : f._type.toLowerCase(),
            opts: options, // not sure why, but we cannot name the property options. It resolves to undefined when you do.
            id: f.id,
            _type: f._type,
            value: f.field.replace("appUser.", "User."),
            ref: f.ref,
          };
        });

        conditions = union(conditions, profileConditionItems);
      }

    }

    conditions.forEach((condition) => {
      condition = this.createConditionItem(condition);
    });

    //console.log('CONDITIONS', conditions)

    return conditions

  }

  private createConditionItem(filter: any) {

    //console.log('CREATE CONDITION ITEM FILTER IN', filter)

    if((filter.comparisons !== undefined && filter.comparisons.length === 0) || filter.comparisons === undefined){
      let comparisons = this.uiService.conditionals[filter._type !== undefined ? filter._type : filter.type]
      if(comparisons === undefined){
        console.log('NO COMPARISONS FOR', filter)
        comparisons = []
      }
      // When the filter is a reference and has a type of select, we need to limit comparison to Equals
      if(filter.ref !== undefined && filter.type === 'select'){
        comparisons = comparisons.filter((c: any) => ['eq','neq'].indexOf(c.value) > -1)
      }
      filter.comparisons = comparisons;
    }

    //console.log('createConditionItem filter comparisons', filter.id, filter.comparisons)

    if (Array.isArray(filter.options) && filter.options.length) {
      if (this.currentAccount.catalog) {
        const catalog = this.currentAccount.catalog;
        const options = get(catalog, filter.options);

        filter.options = options;
      }
    } else {
      if (filter.value === "User.profile.club_level") {
        filter.options = DEFAULT_FILTER_OPTIONS;
      }
    }

    //console.log('CREATE CONDITION ITEM FILTER OUT', filter)

    return filter;
  }

  async initStarterNodeCache() {
    if (!this.starterCacheInitiated) {
     // console.log('INIT START NODE CACHE')
      this.starterCacheInitiated = true;

      await Promise.all([this.initTriggerOptions(), this.initLocationOptions()]);

      this.starterCacheReady.next(true);
    }
  }

  async initPhoneNodeCache() {
    if (!this.phoneCacheInitiated) {
      //console.log('INIT PHONE NODE CACHE')
      this.phoneCacheInitiated = true;

      this.initConversionTrackingOptions();

      await Promise.all([
        this.initTemplateOptions(),
        this.initSignalCategories(),
        this.initTags(),
        this.initLocationOptions(),
      ]);

      this.phoneCacheReady.next(true);
    }
  }

  async initEmailCache() {
    if (!this.integrationCacheInitiated) {
      console.log('INIT EMAIL CACHE')
      
      await Promise.all([
        this.initEmailOptions()
      ]);

      this.integrationCacheInitiated = true;

      this.emailCacheReady.next(true);
      
    }
  }

  async initNotificationNodeCache() {
    if (!this.notificationCacheInitiated) {
      //console.log('INIT NOTIFICATION NODE CACHE')
      await Promise.all([this.initNotificationOptions()]);

      this.notificationCacheReady.next(true);
    }
  }

  invalidateFlowCache() {
    if (this.phoneCacheReady.value) {
      this.phoneCacheReady.next(false);
      this.phoneCacheInitiated = false;
    }

    if (this.notificationCacheReady.value) {
      this.notificationCacheReady.next(false);
      this.notificationCacheInitiated = false;
    }

    if (this.starterCacheReady.value) {
      this.starterCacheReady.next(false);
      this.starterCacheInitiated = false;
    }

    if(this.emailCacheReady.value) {
      this.emailCacheReady.next(false);
      this.integrationCacheInitiated = false;
    }
  }

  private async initTemplateOptions() {
    return new Promise(async (resolve, reject) => {
      const templateOptions = await this.SignalsService.getSignalTemplates(
        this.currentAccount.accountID,
        { page: 0, perPage: 999999999 },
      )
        .pipe(first())
        .toPromise();

      this.templateOptions = (templateOptions as any).records;

      resolve(templateOptions);
    });
  }

  private async initTags() {
    return new Promise(async (resolve, reject) => {
      let tags = { tags: [] };
      try {
        tags = (await this.UtilService.getTags(this.currentAccount.accountID)
          .pipe(first())
          .toPromise()) as unknown as any;
      } catch (e) {
        //IGNORE FOR V2 TESTING
        //TODO REMOVE
        console.log("hit catch");
      }

      this.tags = (tags as unknown as any).tags;

      resolve(this.tags);
    });
  }

  private async initTriggerOptions() {
    return new Promise(async(resolve, reject) => {
      this.getTriggerEvents(this.currentAccount.accountID, {
        page: 0,
        perPage: 9999999
      }).subscribe((data)=>{
        this.triggerOptions = data;
        resolve(data);
      });
    })
  }

  private async initEmailOptions() {
    return new Promise(async(resolve, reject) => {
      resolve(null);
    })
  }

  private async initLocationOptions() {
    return new Promise(async (resolve, reject) => {
      const facilityIDs = this.SessionService.selectedFacilities?.length
        ? this.SessionService.selectedFacilities!.map((facility) => {
            return facility.id;
          })
        : [];

      const geofencesPromise = this.GeofenceService.getGeofences(
        this.currentAccount.accountID,
        {
          facility_id: facilityIDs,
          page: 0,
          perPage: 999999999,
        },
      )
        .pipe(first())
        .toPromise();

      const beaconRegionsPromise = this.BeaconRegionsService.getBeaconRegions(
        this.currentAccount.accountID,
        {
          facility_id: facilityIDs,
          page: 0,
          perPage: 999999999,
        },
      )
        .pipe(take(1))
        .toPromise();

      Promise.all([geofencesPromise, beaconRegionsPromise]).then(
        ([geofences, beaconRegions]) => {
          const geofenceLocations = geofences
            ? (geofences as any).records.map((geofence: any) => {
                return {
                  ...geofence,
                  type: "geofence",
                };
              })
            : [];

          const beaconRegionLocations = beaconRegions
            ? (beaconRegions as any).records.map((geofence: any) => {
                return {
                  ...geofence,
                  type: "beacon-region",
                };
              })
            : [];

          this.locationOptions = [
            ...geofenceLocations,
            ...beaconRegionLocations,
          ].sort((a, b) => {
            return a.name.localeCompare(b.name);
          });

          resolve(this.locationOptions);
        },
      );
    });
  }

  private async initSignalCategories() {
    return new Promise(async (resolve, reject) => {
      const categories = await this.SignalsService.getSignalCategories(
        this.currentAccount.accountID,
      )
        .pipe(first())
        .toPromise();
      this.trackingCategoryOptions = categories as any;

      resolve(categories);
    });
  }

  private initConversionTrackingOptions() {
    this.conversionTrackingOptions = [
      { label: "Day 1", value: "24_hours" },
      { label: "Day 2", value: "48_hours" },
      { label: "Day 3", value: "72_hours" },
      { label: "Day 4", value: "96_hours" },
      { label: "Day 5", value: "5_days" },
      { label: "Day 6", value: "6_days" },
      { label: "Day 7", value: "7_days" },
      { label: "Week 2", value: "2_weeks" },
      { label: "Week 3", value: "3_weeks" },
      { label: "Week 4", value: "1_month" },
    ];
  }

  private async initNotificationOptions() {
    return new Promise(async (resolve, reject) => {
      const notificationOptions: any[] = [];
      const accountUserGroupPromise =
        this.AccountUserGroupService.getAccountUserGroups(
          this.currentAccount.accountID,
        )
          .pipe(first())
          .toPromise();

      const accountUserGroups = await accountUserGroupPromise;

      // Account User Groups
      if (accountUserGroups) {
        (accountUserGroups as any).records.forEach((group: any) => {
          notificationOptions.push({
            type: "AccountUserGroup",
            label: group.name,
            field: group.id,
            id: group.id,
          });
        });
      }

      // Other Options (e.g. Book-of-Business)
      if (this.currentAccount?.catalog) {
        const catalog = this.currentAccount?.catalog;

        if (catalog.signals?.notifications) {
          catalog.signals.notifications.forEach((notification: any) => {
            notificationOptions.push(notification);
          });
        }
      }
      this.notificationOptions = notificationOptions;

      resolve(notificationOptions);
    });
  }

  public async getOfferFieldLookup(field: any) {
    return new Promise(async (resolve, reject) => {
      // request json from the field lookup url
      if(field.lookup.method === "GET"){
        this.httpClient.get(field.lookup.url).subscribe((data: any) => {
          resolve(data.data);
        });
      }
    });
  }

}
